/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { InfoCardKernel, InfoCardKernelType, OfferInfoCardKernel } from 'client-code/actions';

import { InteractiveComponents } from '../../../core/enums';
import { IInfoCard, InfoCardManager } from '../../../core/infocard';
import { NdvrInteractor } from '../../../core/interactors';
import { Log } from '../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { PlaybackDataModelImpl } from '../../../core/playback/impls';
import type { AppDispatch, TGetState } from '../../../core/tivo/tivo-redux-wrapper';
import { TVodPlayActionStateType } from '../../../core/types';
import { ProgramScreenInfoCardUserInputHandler } from '../../../core/usecases/ProgramScreenInfoCardUserInputHandler';
import { UniqueIdentifier } from '../../../core/utils';
import { CcbSwitch } from '../../../core/utils/ccb';
import { CcbSwitchName } from '../../../core/utils/ccb/enums';
import { BiaxialActionWidgetHelper } from '../../../views/ProgramScreen/BiaxialActionWidget/BiaxialActionWidgetHelper';
import { setActiveInteractiveComponent } from '../../interactivelayer/interactiveLayer.reducers';
import { onKeyOkCreateOnePassOverlay } from '../../overlay/createonepassoverlay/createonepassoverlay.reducres';
import {
    onKeyOkDeleteOverlay,
    onMouseClickDeleteRecording,
} from '../../overlay/deleteoverlay/deleteoverlay.reducers';
import {
    onKeyOkKeepUntilRecordingOverlay,
    onMouseClickKeepUntilRecordingOverlay,
} from '../../overlay/keepuntilrecordingoverlay/keepuntillrecordingoverlay.reducers';
import { onKeyOkModifyOnePassOverlay } from '../../overlay/modifyonepassoverlay/modifyonepassoverlay.reducers';
import {
    onKeyLeftOnePassOptionsOverlay,
    onKeyOkCancelOnePassOverlay,
    onKeyOkDOCOverlay,
    onKeyOkOnePassOptionsOverlay,
    onKeyRightOnePassOptionsOverlay,
    onMouseClickDOCOptionsOverlay,
    onMouseClickOnePassOptionsOverlay,
} from '../../overlay/onepassoptionsoverlay/onepassoptionsoverlay.reducers';
import {
    onKeyBackPcPinOverlay,
    onKeyDigitPcPinOverlay,
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyOkPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
} from '../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    onKeyLeftRecordingOverlay,
    onKeyOkRecordingOverlay,
    onKeyOkStopOrCancelRecording,
    onKeyRightRecordingOverlay,
    onMouseClickRecordingOverlay,
    onMouseClickStopOrCancelRecording,
} from '../../overlay/recordingoverlay/recordingoverlay.reducers';
import { onKeyOkStorageLimitOverlay } from '../../overlay/storagelimitoverlay/storagelimitoverlay.reducers';
import {
    onKeyOkStreamingOptionsOverlay,
    onMouseClickStreamingOptionsOverlay,
} from '../../overlay/streamingoptionsoverlay/streamingoptionsoverlay.reducers';
import {
    onKeyOkVodPlayErrorOverlay,
    onMouseClickVodPlayErrorOverlay,
} from '../../overlay/vodplayerroroverlay/vodplayerroroverlay.reducers';
import {
    onKeyOkVodResumeStartOverOverlayForWatchAction,
    onMouseClickVodResumeStartOverOverlayForWatchAction,
} from '../../overlay/vodresumestartoveroverlay/vodresumestartoveroverlay.reducers';
import {
    onKeyOkVodUpSellOverlay,
    onMouseClickVodUpSellOverlay,
} from '../../overlay/vodupselloverlay/vodupselloverlay.reducers';
import {
    hideOverlayDialog,
    onKeyDownOverlayDialog,
    onKeyUpOverlayDialog,
    onMouseOutOverlayDialogOperations,
    onMouseOverOverlayDialogOperations,
} from '../../overlaydialog/overlaydialog.reducers';
import { setScreenTaskController } from '../../screens/screens.reducers';
import { notifyVodPlayActionState } from '../../vodplayaction/vodplayaction.reducers';
import {
    setHoverGradientVisibleAction,
    setProgramScreenCaretVisibleAction,
} from '../programscreen.actions';
import {
    onKeyBackProgramScreenHandler,
    onKeyDownProgramScreenHandler,
    onKeyLeftProgramScreenHandler,
    onKeyOkProgramScreenHandler,
    onKeyPlayProgramScreenHandler,
    onKeyRightProgramScreenHandler,
    onKeyUpProgramScreenHandler,
} from '../programscreen.reducers';
import { ProgramScreenConstants } from '../ProgramScreenConstants';

const keyHandler =
    (overlayCallback: Function, defaultCallback: Function) =>
    (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;

        switch (screenTaskController) {
            case ScreenTaskControllerTypes.MULTI_CANCEL_ONE_PASS_OVERLAY_TASKS:
            case ScreenTaskControllerTypes.DELETE_OVERLAY_TASKS:
            case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
            case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
            case ScreenTaskControllerTypes.GO_TO_OVERLAY_TASKS:
            case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
            case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
            case ScreenTaskControllerTypes.SLO_TASKS:
            case ScreenTaskControllerTypes.STREAMING_OPTIONS_OVERLAY:
            case ScreenTaskControllerTypes.KEEP_UNTIL_RECORDING_OVERLAY:
            case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
            case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            case ScreenTaskControllerTypes.VOD_BROWSE_ERROR_OVERLAY_TASKS:
            case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
                dispatch(overlayCallback());
                break;
            case ScreenTaskControllerTypes.DEFAULT_PROGRAM_SCREEN_TASKS:
                dispatch(defaultCallback());
                break;
            case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
                break;
            default:
        }
    };

export const onKeyBackProgramScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyBackPcPinOverlay());
    } else if (screenTaskController === ScreenTaskControllerTypes.LOADING_STATE_TASK) {
        dispatch(cancelProgramScreenOperation());
        dispatch(keyHandler(hideOverlayDialog, onKeyBackProgramScreenHandler));
    } else {
        dispatch(keyHandler(hideOverlayDialog, onKeyBackProgramScreenHandler));
    }
};

export const onKeyDownProgramScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyDownPcPinOverlay());
    } else if (screenTaskController === ScreenTaskControllerTypes.LOADING_STATE_TASK) {
        dispatch(cancelProgramScreenOperation());
        dispatch(keyHandler(onKeyDownOverlayDialog, onKeyDownProgramScreenHandler));
    } else {
        dispatch(keyHandler(onKeyDownOverlayDialog, onKeyDownProgramScreenHandler));
    }
};

export const onKeyDigitProgramScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyDigitPcPinOverlay());
    } else if (screenTaskController === ScreenTaskControllerTypes.LOADING_STATE_TASK) {
        cancelProgramScreenOperation();
    }
};

export const onKeyInfoProgramScreen = () => (dispatch: Function, getState: TGetState) => {
    if (
        CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_INFO_CARD_REMOVE_IN_KST_12193,
        )
    ) {
        const { screenTaskController } = getState().screens;
        if (screenTaskController !== ScreenTaskControllerTypes.DEFAULT_PROGRAM_SCREEN_TASKS) return;
        const { currentHighlightIndex, horizontalOptions } = getState().programScreen;
        const { displayDetails } = horizontalOptions[currentHighlightIndex];
        const infoCard: IInfoCard = InfoCardManager.getInstance();

        let infoCardKernel: InfoCardKernel;
        if (
            [
                ProgramScreenConstants.SEASONS,
                ProgramScreenConstants.VIEW_ALL,
                ProgramScreenConstants.EMPTY_STRIP,
                ProgramScreenConstants.LOADING_STRIP,
            ].includes(displayDetails.identifier)
        )
            return;
        if (
            [
                ProgramScreenConstants.UPCOMING_AIRING,
                ProgramScreenConstants.ALL_UPCOMING_EPISODES,
            ].includes(displayDetails.identifier)
        ) {
            infoCardKernel = {
                infoCardKernelType: InfoCardKernelType.OfferInfoCardKernel,
                offerId: displayDetails.offerId,
            } as OfferInfoCardKernel;
            const totalCount = BiaxialActionWidgetHelper.getTotalCount()!;
            const programScreenInfoCardUserInputHandler =
                new ProgramScreenInfoCardUserInputHandler();
            infoCard.addUserInputEventHandler(programScreenInfoCardUserInputHandler);
            infoCard.setProperties({
                hasItemsBefore: totalCount > 2,
                hasItemsAfter: totalCount > 2,
            });
            infoCard.setContextData(ProgramScreenInfoCardUserInputHandler.name, {
                offset: BiaxialActionWidgetHelper.getCurrentImageIndex(),
                id: BiaxialActionWidgetHelper.getId(),
                totalCount: totalCount - 1, // remove view all
            });
        } else if (displayDetails.action) {
            infoCardKernel = displayDetails.action.infoCardKernel;
        } else {
            Log.e(
                'ProgramScreenNavigationReducers',
                'onKeyInfo: No suitable infocard kernel found to trigger info card',
            );
            return;
        }
        infoCard.setInfoCardKernel(infoCardKernel);
        infoCard.show();
    }
};

export const onLongKeyOkProgramScreen = () => (dispatch: Function) => {
    dispatch(onKeyInfoProgramScreen());
};

export const onMouseOkLongPressStartProgramScreen = () => (dispatch: Function) => {
    dispatch(onLongKeyOkProgramScreen());
};

export const onKeyLeftProgramScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyLeftOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_PROGRAM_SCREEN_TASKS:
            dispatch(onKeyLeftProgramScreenHandler());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyLeftPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onKeyLeftRecordingOverlay());
            break;
        case ScreenTaskControllerTypes.LOADING_STATE_TASK:
            dispatch(cancelProgramScreenOperation());
            dispatch(onKeyLeftProgramScreenHandler());
            break;
        default:
    }
};

export const onKeyOkProgramScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.STREAMING_OPTIONS_OVERLAY: {
            const { actionDetails } = PlaybackDataModelImpl.getInstance().getActionDetailsList();
            dispatch(onKeyOkStreamingOptionsOverlay(actionDetails));
            break;
        }
        case ScreenTaskControllerTypes.MULTI_CANCEL_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyOkCancelOnePassOverlay());
            break;
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyOkDOCOverlay());
            break;
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyOkCreateOnePassOverlay());
            break;
        case ScreenTaskControllerTypes.KEEP_UNTIL_RECORDING_OVERLAY:
            dispatch(onKeyOkKeepUntilRecordingOverlay());
            break;
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onKeyOkRecordingOverlay());
            break;
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
            dispatch(onKeyOkStopOrCancelRecording());
            break;
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyOkModifyOnePassOverlay());
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyOkOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyOkPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.SLO_TASKS:
            dispatch(onKeyOkStorageLimitOverlay());
            break;
        case ScreenTaskControllerTypes.DELETE_OVERLAY_TASKS:
            dispatch(onKeyOkDeleteOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyOkVodResumeStartOverOverlayForWatchAction());
            break;
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onKeyOkVodUpSellOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_PROGRAM_SCREEN_TASKS:
            dispatch(onKeyOkProgramScreenHandler());
            break;
        case ScreenTaskControllerTypes.LOADING_STATE_TASK:
            dispatch(cancelProgramScreenOperation());
            dispatch(onKeyOkProgramScreenHandler());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.FORMAT_SELECTOR_KEY_OK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.VOD_RESUME_OVERLAY_KEY_OK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onKeyOkVodPlayErrorOverlay());
            break;
        default:
    }
};

export const onKeyPlayProgramScreen = () => (dispatch: AppDispatch, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.DEFAULT_PROGRAM_SCREEN_TASKS) {
        dispatch(onKeyPlayProgramScreenHandler());
    }
};

export const onKeyRightProgramScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyRightOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_PROGRAM_SCREEN_TASKS:
            dispatch(onKeyRightProgramScreenHandler());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyRightPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onKeyRightRecordingOverlay());
            break;
        case ScreenTaskControllerTypes.LOADING_STATE_TASK:
            dispatch(cancelProgramScreenOperation());
            dispatch(onKeyRightProgramScreenHandler());
            break;
        default:
    }
};

export const onKeyUpProgramScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyUpPcPinOverlay());
    } else {
        dispatch(keyHandler(onKeyUpOverlayDialog, onKeyUpProgramScreenHandler));
    }
};

export const onMouseClickProgramScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { inputFactory } = getState();
    const { value } = inputFactory;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.MULTI_CANCEL_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyOkCancelOnePassOverlay());
            break;
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            dispatch(onMouseClickDOCOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyOkCreateOnePassOverlay());
            break;
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            if (value === 'HideRecordingOverlay') {
                dispatch(onKeyBackProgramScreen());
            } else {
                dispatch(onMouseClickRecordingOverlay());
            }
            break;
        case ScreenTaskControllerTypes.DELETE_OVERLAY_TASKS:
            dispatch(onMouseClickDeleteRecording());
            break;
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
            dispatch(onMouseClickStopOrCancelRecording());
            break;
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyOkModifyOnePassOverlay());
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            if (value === 'HideOnePassOptionsOverOverlay') {
                dispatch(onKeyBackProgramScreen());
            } else {
                dispatch(onMouseClickOnePassOptionsOverlay());
            }
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseClickPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.SLO_TASKS:
            dispatch(onKeyOkStorageLimitOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onMouseClickVodResumeStartOverOverlayForWatchAction());
            break;
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onMouseClickVodUpSellOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_PROGRAM_SCREEN_TASKS:
            dispatch(onMouseClickProgramScreenHelper());
            break;
        case ScreenTaskControllerTypes.STREAMING_OPTIONS_OVERLAY:
            if (value === InteractiveComponents.NORTH) {
                dispatch(onKeyUpOverlayDialog());
            } else if (value === InteractiveComponents.SOUTH) {
                dispatch(onKeyDownOverlayDialog());
            } else {
                const { actionDetails } =
                    PlaybackDataModelImpl.getInstance().getActionDetailsList();
                dispatch(onMouseClickStreamingOptionsOverlay(actionDetails));
            }
            break;
        case ScreenTaskControllerTypes.KEEP_UNTIL_RECORDING_OVERLAY:
            dispatch(onMouseClickKeepUntilRecordingOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.FORMAT_SELECTOR_MOUSE_CLICK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.VOD_RESUME_OVERLAY_MOUSE_CLICK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onMouseClickVodPlayErrorOverlay());
            break;
        default:
    }
};

export const onMouseClickProgramScreenHelper = () => (dispatch: Function, getState: Function) => {
    const { inputFactory, programScreen } = getState();

    const { value } = inputFactory;
    const { currentVerticalHighlightIndex, totalVerticalItems } = programScreen;
    if (value === 'list-container-centre') {
        dispatch(onKeyOkProgramScreenHandler());
    } else if (value === InteractiveComponents.NORTH) {
        dispatch(onKeyUpProgramScreen());
        if (currentVerticalHighlightIndex === 1)
            dispatch(setProgramScreenCaretVisibleAction(false));
    } else if (value === InteractiveComponents.SOUTH) {
        dispatch(onKeyDownProgramScreen());
        // checking currentVerticalHighlightIndex before updating in state, hence checking with + 2
        if (currentVerticalHighlightIndex + 2 === totalVerticalItems) {
            dispatch(setProgramScreenCaretVisibleAction(false));
        }
    } else if (value === InteractiveComponents.WEST || value === 'list-container-west') {
        dispatch(onKeyLeftProgramScreen());
    } else if (value === InteractiveComponents.EAST || value === 'list-container-east') {
        dispatch(onKeyRightProgramScreen());
    }
};

export const onMouseOutHelper = () => (dispatch: Function, getState: Function) => {
    const { inputFactory } = getState();
    const { value } = inputFactory;
    if (
        value === InteractiveComponents.NORTH ||
        value === InteractiveComponents.SOUTH ||
        value === InteractiveComponents.EAST ||
        value === InteractiveComponents.WEST ||
        value === 'list-container-centre'
    ) {
        dispatch(setProgramScreenCaretVisibleAction(false));
    } else if (value === 'list-container-east' || value === 'list-container-west') {
        dispatch(setHoverGradientVisibleAction(false));
        dispatch(setProgramScreenCaretVisibleAction(false));
    }
};

export const onMouseOutProgramScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.MULTI_CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.DELETE_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.STREAMING_OPTIONS_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.KEEP_UNTIL_RECORDING_OVERLAY:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onMouseOutOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.DEFAULT_PROGRAM_SCREEN_TASKS:
            dispatch(onMouseOutHelper());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOutPcPinOverlay());
            break;
        default:
    }
};

export const onMouseOverProgramScreenHelper = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    const { programScreen } = getState();
    const { currentVerticalHighlightIndex, totalItems, totalVerticalItems } = programScreen;
    if (value === InteractiveComponents.NORTH) {
        if (currentVerticalHighlightIndex === 0)
            dispatch(setProgramScreenCaretVisibleAction(false));
        else {
            dispatch(setActiveInteractiveComponent(value));
            dispatch(setProgramScreenCaretVisibleAction(true));
        }
    } else if (value === InteractiveComponents.SOUTH) {
        if (currentVerticalHighlightIndex + 1 === totalVerticalItems) {
            dispatch(setProgramScreenCaretVisibleAction(false));
        } else {
            dispatch(setActiveInteractiveComponent(value));
            dispatch(setProgramScreenCaretVisibleAction(true));
        }
    } else if (value === 'list-container-east' || value === InteractiveComponents.EAST) {
        if (totalItems === 1) {
            dispatch(setProgramScreenCaretVisibleAction(false));
        } else {
            dispatch(setActiveInteractiveComponent(InteractiveComponents.EAST));
            dispatch(setHoverGradientVisibleAction(true));
            dispatch(setProgramScreenCaretVisibleAction(true));
        }
    } else if (value === 'list-container-west' || value === InteractiveComponents.WEST) {
        if (totalItems === 1) {
            dispatch(setProgramScreenCaretVisibleAction(false));
        } else {
            dispatch(setActiveInteractiveComponent(InteractiveComponents.WEST));
            dispatch(setHoverGradientVisibleAction(true));
            dispatch(setProgramScreenCaretVisibleAction(true));
        }
    } else if (value === 'list-container-centre') {
        dispatch(setActiveInteractiveComponent(''));
        if (totalItems === 1) {
            dispatch(setProgramScreenCaretVisibleAction(false));
        } else {
            dispatch(setHoverGradientVisibleAction(false));
            dispatch(setProgramScreenCaretVisibleAction(true));
        }
    }
};

export const onMouseOverProgramScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.MULTI_CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.DELETE_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.STREAMING_OPTIONS_OVERLAY:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.KEEP_UNTIL_RECORDING_OVERLAY:
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onMouseOverOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.DEFAULT_PROGRAM_SCREEN_TASKS:
            dispatch(onMouseOverProgramScreenHelper());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOverPcPinOverlay());
            break;
        default:
    }
};

const cancelProgramScreenOperation = () => (dispatch: Function) => {
    const services = new NdvrInteractor();
    services.getRecordingOfferIdOptionsRequestModel('', '').drop();
    UniqueIdentifier.getNextOnePassServiceNetworkCallId();
    UniqueIdentifier.getNextOttActionDetailsNetworkCallId();
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_PROGRAM_SCREEN_TASKS));
};
